<template>
  <div style="height: 30px;">
    <a :href="'https://new.getkairo.com?utm_source=kairo-embed-paid&utm_medium=embed&utm_campaign=embed-' + embedId"
       class="text-xs leading-9 hover:underline"
       :class="{'text-gray-400' : darkMode, 'text-gray-500': !  darkMode}"
       target="_blank">
      <svg xmlns="http://www.w3.org/2000/svg" class="text-yellow-500 h-3 w-3 inline -mt-0.5" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd" />
      </svg>
      by Kairo
    </a>
  </div>
  <component
    v-if="block"
    :is="block.type || 'Block'"
    v-bind="block"
    :embed="true"
    :isLoading="isLoading"
    :hasError="hasError"
    :blockTick="blockTick"
    @element-start="elementStart"
    @element-end="elementEnd"
    @element-increase="elementIncrease"
    @element-decrease="elementDecrease"
    @countdown-start="countdownStart"
    @countdown-end="countdownEnd"
    @block-notification-update="blockNotificationUpdate"
    @block-reset="blockReset"
  />
</template>

<script>
  import BlockCounter from '@/components/BlockCounter.vue';
  import BlockProgress from '@/components/BlockProgress.vue';
  import BlockCountdown from '@/components/BlockCountdown.vue';
  import Block from '@/components/Block.vue';
  import {v4 as uuidv4} from "uuid";
  import {animationInterval} from "../utils/animationInterval";
  import Worker from "worker-loader!../workers/countdown.js";
  import { buildEdgeFunctionUrl } from '@/helpers/edge-functions';

  function resolveEmbedIdFromLocation() {
    const params = new URLSearchParams(window.location.search);
    const queryEmbedId = params.get('id');

    if (queryEmbedId) {
      return queryEmbedId;
    }

    const pathMatch = window.location.pathname.match(/^\/embed\/([^/]+)$/);
    return pathMatch ? decodeURIComponent(pathMatch[1]) : null;
  }

  const sfx = require('@/assets/sfx/alarm.mp3');

  export default {
    name: 'Embed',
    components: {
      Block,
      BlockCounter,
      BlockProgress,
      BlockCountdown
    },
    data() {
      return {
        embedId: null,
        darkMode: false,
        isLoaded: false,
        blocks: this.$store.state.embed.blocks,
        jobQueue: [],
        jobQueueInterval: '',
        jobQueueRunning: false,
        audio: null,
        sfx: sfx,
        hasError: false,
        controller: new AbortController(),
        blockTick: new Date().getTime() + 1000,
        worker: null
      }
    },
    computed: {
      block() {
        let block = this.blocks.find(item => item.embedId === this.embedId);
        if (block) {
          return block;
        } else {
          return null;
        }
      },
      isLoading() {
        return !!this.jobQueue.length;
      }
    },
    methods: {
      blockIncrement(blockId) {
        this.$store.commit('blockIncrement', blockId);
      },
      elementStart (blockId, trackType = 'track') {
        // stop any running block - there should always be just one
        let index = this.blocks.findIndex(item => item.id === blockId );
        const randomId = uuidv4();

        const element = {
          id: randomId,
          type: trackType,
          start: new Date().getTime(),
          end: null,
          size: null
        };

        const elements = JSON.parse(JSON.stringify(this.blocks[index].elements));

        this.$store.commit('elementStart', {
          index: index,
          element: element
        });

        const blockData = {
          elements: elements,
          activeSince: element.start,
          totalTime: this.blocks[index].totalTime,
          id: this.blocks[index].id
        };
        blockData.elements.push(element);
        if (!this.local) {
          this.updateBlock(blockData);
        }
      },
      elementEnd (blockId) {
        let index = this.blocks.findIndex(item => item.id === blockId );
        let n = new Date().getTime();
        let length = this.blocks[index].elements.length - 1;

        this.$store.commit('elementEnd', {
          index: index,
          last: length,
          end: n
        });

        if (!this.local) {
          const elements = JSON.parse(JSON.stringify(this.blocks[index].elements));
          const blockData = {
            elements: elements,
            activeSince: 0,
            totalTime: 0,
            id: this.blocks[index].id
          };
          blockData.elements[length].end = n;
          const total = this.calculateElementsTotal(blockData.elements);
          blockData.totalTime = total;

          this.updateBlock(blockData);
        }
      },
      elementIncrease (blockId, incTitle, incIcon, incValue) {
        let index = this.blocks.findIndex(item => item.id === blockId );
        const randomId = uuidv4();

        const numericValue = Number(incValue);
        const fallbackValue = this.blocks[index] && typeof this.blocks[index].increaseValue !== 'undefined'
          ? Number(this.blocks[index].increaseValue)
          : 1;
        const value = Number.isFinite(numericValue) ? numericValue : (Number.isFinite(fallbackValue) ? fallbackValue : 1);

        const element = {
          id: randomId,
          start: new Date().getTime(),
          value: value,
          title: incTitle,
          icon: incIcon
        };

        this.$store.commit('elementUpdate', {
          index: index,
          element: element
        });
        if (!this.local) {
          this.jobQueueAdd({event: 'blockUpdate_' + blockId, expiration: 2000});
        }
      },
      elementDecrease (blockId) {
        let index = this.blocks.findIndex(item => item.id === blockId );

        this.$store.commit('elementRemove', {
          index: index
        });

        if (!this.local) {
          this.jobQueueAdd({event: 'blockUpdate_' + blockId, expiration: 2000});
        }
      },
      countdownIncrement(blockId) {
        this.$store.commit('countdownIncrement', blockId);
      },
      countdownStart (blockId) {
        let index = this.blocks.findIndex(item => item.id === blockId );
        this.$store.commit('countdownStart', {
          index: index,
          start: new Date().getTime()
        });

        if (!this.local) {
          this.jobQueueAdd({event: 'blockUpdate_' + blockId, expiration: 2000});
        }
      },
      countdownEnd (blockId, options = {}) {
        this.soundStop();
        let index = this.blocks.findIndex(item => item.id === blockId );
        this.$store.commit('countdownEnd', {
          index: index,
          elapsedTime: options.preserveElapsedTime ? options.elapsedTime : undefined
        });

        if (!this.local) {
          this.jobQueueAdd({event: 'blockUpdate_' + blockId, expiration: 2000});
        }
      },
      calculateElementsTotal(elements) {
        let total = 0;
        elements.forEach((element) => {
          total = total + (element.end - element.start);
        });
        return total;
      },
      blockNotificationUpdate(blockId, status) {
        this.$store.commit('blockNotificationUpdate', {
          id: blockId,
          status: status
        });
      },
      fetchBlock() {
        if (this.block !== null) {
          this.$store.commit('blockAdd', this.block);
        }
        this.isLoaded = false;
        fetch(buildEdgeFunctionUrl('embed', { id: this.embedId }), {
          method: 'POST',
        }).then(response => response.json())
            .then(data => {
              if (data.id) {
                data.data.refID = data.id;
                data.data.id = data.id;
                this.$store.commit('blockAdd', data.data);
                if (data.data.embedOptions && data.data.embedOptions.darkMode) {
                  this.darkMode = data.data.embedOptions.darkMode;
                  document.body.classList.add('embed-dark-mode');
                }
              } else {
                this.$store.commit('blockDelete', this.embedId);
                this.isLoaded = true;
              }
            })
            .catch(() => {
              this.$store.commit('blockDelete', this.embedId);
              this.isLoaded = true;
              this.status = false;
            });
      },
      blockReset(blockId) {
        this.$store.commit('blockReset', blockId);

        if (this.block && this.block.type === 'Block') {
          this.block.blockTick = new Date().getTime() + 1000;
        }

        if (!this.local && this.block && this.block.embedOptions && this.block.embedOptions.mode === 'update') {
          const blockRaw = JSON.parse(JSON.stringify(this.block));
          this.updateBlock({
            id: blockRaw.id,
            elements: blockRaw.elements,
            activeSince: blockRaw.activeSince,
            activeCountdownSince: blockRaw.activeCountdownSince,
            totalTime: blockRaw.totalTime,
            elapsedTime: blockRaw.elapsedTime,
            notificationSent: blockRaw.notificationSent,
            __trackingSync: {
              preserveHistory: true,
              resetTs: Date.now(),
            },
          });
        }
      },
      updateBlock(blockUpdate = false) {
        if (!blockUpdate) {
          const blockRaw = JSON.parse(JSON.stringify(this.block));
          blockUpdate = {
            id: blockRaw.id,
            elements: blockRaw.elements
          };
        }

        fetch(buildEdgeFunctionUrl('embed-update', { id: this.block.embedId }), {
          method: 'POST',
          body: JSON.stringify(blockUpdate)
        }).then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            throw new Error('Something went wrong');
          }
        })
        .then(() => {
          // We do nothing here
        })
        .catch(() => {
          this.hasError = true;
        });
      },
      jobQueueAdd(job) {
        let index = this.jobQueue.findIndex(item => item.event === job.event);
        if (index >= 0) {
          let timeObject = new Date().getTime();
          const expiration = timeObject + job.expiration;
          this.jobQueue[index].expiration = expiration;
        } else {
          let timeObject = new Date().getTime();
          const expiration = timeObject + job.expiration;
          this.jobQueue.push({id: uuidv4(), event: job.event, expiration: expiration});
        }
      },
      jobQueueProcess() {
        // execute only if jobQueue is not running
        if (!this.jobQueueRunning) {
          this.jobQueueRunning = true;
          const jobsToRun = JSON.parse(JSON.stringify(this.jobQueue));
          //this.jobQueue.splice(0, this.jobQueue.length); // clear the queue so other events can be added and processed later

          jobsToRun.forEach((job) => {
            let timeNow = new Date().getTime();
            if (job.expiration < timeNow) {
              let jobEvent = job.event.split("_");
              if (jobEvent[0] === "blockUpdate") {
                let job_index = this.jobQueue.findIndex(item => item.id === job.id);
                this.jobQueue.splice(job_index, 1);
                let block_index = this.blocks.findIndex(item => item.id === jobEvent[1]);
                if (block_index >= 0) {
                  const blockData = this.blocks[block_index];
                  this.updateBlock(blockData);
                }
              }
            }
          });
          this.jobQueueRunning = false;
        }
      },
      soundPlay() {
        this.audio = new Audio(sfx);
        this.audio.loop = true;
        this.audio.play();
      },
      soundStop() {
        if (this.audio !== null) {
          this.audio.pause();
          this.audio = null;
        }
      },
    },
    created() {
      let params = new URLSearchParams(window.location.search);
      this.embedId = resolveEmbedIdFromLocation();
      this.local = params.get("local");

      if (this.local) {
        this.darkMode = !!params.get("darkMode");
        if (this.darkMode) {
          document.body.classList.add('embed-dark-mode');
        } else {
          document.body.classList.add('embed-light-mode');
        }
      }

      animationInterval(1000, this.controller.signal, () => {
        if (this.block && this.block.activeSince) {
          this.blockIncrement(this.block.id);
        }

        const blockCountdownActive = this.blocks.filter(block => block.activeCountdownSince > 0);
        if (blockCountdownActive.length > 0) {
          blockCountdownActive.forEach((block) => {
            this.countdownIncrement(block.id);
          });
        }

        this.blockTick = new Date().getTime() + 1000;
      });
    },
    mounted() {
      this.jobQueueInterval = setInterval(() => {
        this.jobQueueProcess();
      }, 1000);
      if (!this.local) {
        this.fetchBlock();
      } else {
        // this is a local block
        let params = new URLSearchParams(window.location.search);

        //embed?id=a735f6de-e478-4d38-b408-7c711981df2f&local=true&title=Water&type=BlockCounter&color=blue&size=2200&increaseValue=50&faceType=default

        const blockData = {
          "id": this.embedId, // URL Param
          "title": params.get("title"), // URL Param
          "type": params.get("type"), // URL Param
          "color": params.get("color"), // URL Param
          "size": params.get("size") / 1, // URL Param
          "increaseValue": params.get("increaseValue") / 1, // URL Param
          "faceType": params.get("faceType"), // URL Param
          "skin": params.get("skin"), // URL Param
          "elapsedTime": 0,
          "totalTime": 0,
          "activeSince": 0,
          "activeCountdownSince": 0,
          "elements": [],
          "progressStart": params.get("progressStart") / 1, // URL Param
          "progressEnd": params.get("progressEnd") / 1, // URL Param
          "counters": JSON.parse(params.get("counters")), // URL Param
          "embedId": this.embedId, // URL Param
          "embedOptions": {
            "mode": "update",
            "darkMode": !!params.get("darkMode")
          },
          "local": true
        };

        // merge block with locally stored values if they are there
        let block = this.blocks.find(item => item.embedId === this.embedId);

        if (block) {
          blockData.elapsedTime = JSON.parse(JSON.stringify(block.elapsedTime)) / 1;
          blockData.totalTime = JSON.parse(JSON.stringify(block.totalTime)) / 1;
          blockData.activeSince = JSON.parse(JSON.stringify(block.activeSince)) / 1;
          blockData.activeCountdownSince = JSON.parse(JSON.stringify(block.activeCountdownSince)) / 1;
          blockData.elements = JSON.parse(JSON.stringify(block.elements));
        }

        this.$store.commit('blockAdd', blockData);
      }

      // Worker
      if (window.Worker) {
        this.worker = new Worker();
        this.worker.postMessage('');
        this.worker.onmessage = () => {
          const blockCountdownActive = this.blocks.filter(block => block.activeCountdownSince > 0);
          if (blockCountdownActive.length > 0) {
            blockCountdownActive.forEach((block) => {
              if (!block.notificationSent) {
                let fullTime = block.size;
                const now = new Date().getTime();
                const elapsed = (now - block.activeCountdownSince) / 1000;
                fullTime = fullTime - elapsed;
                if (fullTime <= 0) {
                  if (this.audio === null) {
                    this.soundPlay();
                  }
                  this.blockNotificationUpdate(block.id, true);
                  this.nativeNotification.show(
                      'Kairo: Time is up',
                      {
                        body: block.title + ' has finished.',
                        requireInteraction: true
                      },
                      {},
                  );
                  this.countdownEnd(block.id, {
                    preserveElapsedTime: true,
                    elapsedTime: Number(block.size) * 1000,
                  });
                }
              }
            });
          }
        };
      }
    },
    beforeUnmount () {
      clearInterval(this.jobQueueInterval);
    },
    unmounted() {
      this.controller.abort();
      this.worker.terminate();
    }
  };
</script>
<style>
  .embed-light-mode {
    background-color: #ffffff;
  }
  .embed-dark-mode {
    background-color: #191919;
  }
</style>
